import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { PublicLinkTableProps, PublicLinkItemData } from '../types';
import { getTableColumns } from '../constants';
import AccountCell from './AccountCell';
import StatisticsCell from './StatisticsCell';
import IncomeCell from './IncomeCell';
import StatusCell from './StatusCell';

const PublicLinkTable: React.FC<PublicLinkTableProps> = ({
  data,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslations();

  // Проверяем, есть ли хоть у одного элемента данные о транзакциях
  const hasAnyTransactionStats = data.some(
    (item) => item.transactions_statistic && item.transactions_statistic.total !== undefined
  );

  const renderCell = useCallback((item: PublicLinkItemData, columnKey: string) => {
    switch (columnKey) {
      case 'account':
        return <AccountCell item={item} />;
      case 'statistics':
        return <StatisticsCell item={item} />;
      case 'income':
        return <IncomeCell item={item} />;
      case 'status':
        return <StatusCell item={item} />;
      default:
        return null;
    }
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  // Фильтруем колонки: убираем income если нет данных о транзакциях
  const columns = getTableColumns(t).filter(
    (col) => col.key !== 'income' || hasAnyTransactionStats
  );

  return (
    <UniversalTable
      data={data}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('table.noDataFound')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('table.checkDataOrTryAgain')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default PublicLinkTable;
